import React, { useEffect, useState, useCallback } from "react";
import { exportAccount, getDeletionPreflight } from "@/api";
import DeleteAccountFlow from "@/components/DeleteAccountFlow";

// Read-only Account panel. Performs ONLY GET calls (export + deletion preflight).
// No POST, no deletion prepare/confirm, no DCC/fence creation. Identity is the
// authenticated session only — no client-supplied email/id is ever sent.

// Plain-language labels for what account deletion would cover. We deliberately do
// NOT surface internal collection names / schema / feature-flag details.
const DELETE_LABELS = {
  account_profile: "Your profile and account details",
  discovery_findability: "Your Findability check answers",
  canonical_cycles: "Your Findability Cycles",
  baselines: "Your cycle baselines",
  efforts: "Your completed efforts",
  progress_reviews: "Your progress reviews",
  canonical_vault_items: "Your saved posts (Vault)",
  legacy_beta_record: "Earlier beta records",
  generation_usage: "Your post-generation history",
  authentication_data: "Your sign-in data",
};

const RETAIN_LABELS = {
  purchase_events: "Payment records",
  entitlements: "Purchase credits",
  entitlement_audit_history: "Payment audit history",
};

function labelFor(map, category) {
  return map[category] || category.replace(/_/g, " ");
}

export default function AccountPanel({ email, onClose, onSignOut }) {
  const [preflight, setPreflight] = useState(null);
  const [pfLoading, setPfLoading] = useState(true);
  const [pfError, setPfError] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(false);
  const [exportedOk, setExportedOk] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadPreflight = useCallback(async () => {
    setPfLoading(true);
    setPfError(false);
    try {
      const data = await getDeletionPreflight();
      setPreflight(data);
    } catch (e) {
      setPfError(true);
    } finally {
      setPfLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPreflight();
  }, [loadPreflight]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleExport = async () => {
    setExporting(true);
    setExportError(false);
    setExportedOk(false);
    try {
      const data = await exportAccount();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "findability-account-export.json";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportedOk(true);
    } catch (e) {
      setExportError(true);
    } finally {
      setExporting(false);
    }
  };

  const deletable = (preflight?.inventory?.deletable || []).filter((d) => DELETE_LABELS[d.category] || d.category !== "authentication_data");
  const retention = preflight?.inventory?.retention || [];
  const canConfirm = preflight?.can_confirm_deletion;
  const blockers = preflight?.blockers || [];
  const warnings = [
    ...(preflight?.warnings || []),
    ...((preflight?.entitlements?.warnings) || []),
  ];

  return (
    <div className="acct-overlay" onMouseDown={onClose} data-testid="account-overlay">
      <div className="acct-modal" onMouseDown={(e) => e.stopPropagation()} data-testid="account-panel" role="dialog" aria-modal="true" aria-label="Account">
        <button className="acct-close" onClick={onClose} data-testid="account-close" aria-label="Close">×</button>

        <div className="acct-eyebrow"><span className="eyebrow-dot" /> Account</div>
        <h2 className="acct-title">Your <em>account</em></h2>
        <p className="acct-email" data-testid="account-email">{email}</p>

        {!deleting ? (<>
        {/* ── Download my data ── */}
        <section className="acct-section" data-testid="account-export-section">
          <h3 className="acct-h3">Download my data</h3>
          <p className="acct-help">Export/download only — this does not delete or change your account.</p>
          <button
            className="acct-btn acct-btn-primary"
            onClick={handleExport}
            disabled={exporting}
            data-testid="account-export-btn"
          >
            {exporting ? "Preparing…" : "Download my data"}
          </button>
          {exportedOk && (
            <p className="acct-note-ok" data-testid="account-export-ok">Your export has been downloaded.</p>
          )}
          {exportError && (
            <p className="acct-note-err" data-testid="account-export-error">
              We couldn't prepare your export just now.{" "}
              <button className="acct-link" onClick={handleExport} data-testid="account-export-retry">Try again</button>
            </p>
          )}
        </section>

        {/* ── Account deletion information (read-only) ── */}
        <section className="acct-section" data-testid="account-deletion-info">
          <h3 className="acct-h3">Account deletion information</h3>

          {pfLoading && <p className="acct-help" data-testid="account-preflight-loading">Loading…</p>}

          {pfError && (
            <p className="acct-note-err" data-testid="account-preflight-error">
              We couldn't load this information right now.{" "}
              <button className="acct-link" onClick={loadPreflight} data-testid="account-preflight-retry">Try again</button>
            </p>
          )}

          {!pfLoading && !pfError && preflight && (
            <div data-testid="account-preflight-content">
              <div className="acct-block">
                <div className="acct-block-title">What would be permanently deleted</div>
                <ul className="acct-list" data-testid="account-delete-list">
                  {deletable.map((d) => (
                    <li key={d.category}>
                      {labelFor(DELETE_LABELS, d.category)}
                      {d.count > 0 && <span className="acct-count">{d.count}</span>}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="acct-block">
                <div className="acct-block-title">What may be kept or minimized</div>
                <p className="acct-help">
                  Some limited records may be retained — in a minimized form no longer linked to your
                  live account — where required for legal, accounting, payment, and audit purposes.
                </p>
                <ul className="acct-list acct-list-muted" data-testid="account-retain-list">
                  {retention.map((r) => (
                    <li key={r.category}>{labelFor(RETAIN_LABELS, r.category)}</li>
                  ))}
                </ul>
              </div>

              {!canConfirm && blockers.length > 0 && (
                <div className="acct-block acct-block-block" data-testid="account-deletion-blocked">
                  <div className="acct-block-title">Before deletion can proceed</div>
                  <ul className="acct-list">
                    {blockers.map((b, i) => (<li key={i}>{b}</li>))}
                  </ul>
                </div>
              )}

              {warnings.length > 0 && (
                <div className="acct-warnings" data-testid="account-warnings">
                  {warnings.map((w, i) => (
                    <p key={i} className="acct-warn-line">{w}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        {/* ── Delete account (actionable entry into the staged flow) ── */}
        <section className="acct-section" data-testid="account-delete-section">
          <div className="acct-delete-row">
            <div>
              <div className="acct-delete-label acct-danger-title">Delete account</div>
              <p className="acct-help" style={{ margin: 0 }}>
                Permanently remove your account and all of your Findability work.
              </p>
            </div>
            <button
              className="acct-btn acct-btn-danger-on"
              onClick={() => setDeleting(true)}
              disabled={!preflight}
              data-testid="account-delete-start-btn"
            >
              Delete account
            </button>
          </div>
        </section>
        </>) : (
          <section className="acct-section" data-testid="account-delete-flow">
            <DeleteAccountFlow
              preflight={preflight}
              onCancel={() => setDeleting(false)}
              onDeleted={() => { onClose(); if (onSignOut) onSignOut(); }}
            />
          </section>
        )}
      </div>
    </div>
  );
}
